import React, { useState, useEffect } from "react";
import {
  X,
  User,
  Lock,
  Eye,
  EyeOff,
  Fingerprint,
  DollarSign,
  Wallet,
  Calendar,
  CalendarDays,
  Briefcase,
  CheckCircle,
  XCircle,
  Receipt,
  Ban,
  MapPin,
  Info,
  Edit3,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

export default function BossUpdateEmployeeModal({
  visible,
  onClose,
  employee,
  onUpdateSuccess,
}) {
  const [name, setName] = useState("");
  const [dailySalary, setDailySalary] = useState("");
  const [monthlySalary, setMonthlySalary] = useState("");
  const [password, setPassword] = useState("");
  const [type, setType] = useState(1); // 1 = Employee, 2 = Manager
  const [typeSalary, setTypeSalary] = useState(1); // 1 = Daily, 2 = Monthly
  const [active, setActive] = useState(1); // 1 = Active, 2 = Inactive
  const [accessForExpenses, setAccessForExpenses] = useState(2); // 1 = Has Access, 2 = No Access
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [allData, setAllData] = useState(null);

  useEffect(() => {
    // Load allData from localStorage
    const data = localStorage.getItem("allData");
    if (data) {
      setAllData(JSON.parse(data));
    }
  }, []);

  useEffect(() => {
    if (employee) {
      setName(employee.name || "");
      setPassword(employee.passWord || "");
      setType(employee.type || 1);
      setActive(employee.active || 1);
      setAccessForExpenses(employee.accessForExpenses || 2);

      // Parse existing access for places
      if (employee.accessForPlaces) {
        const accessIds = employee.accessForPlaces
          .split(",")
          .map((id) => parseInt(id.trim()))
          .filter((id) => !isNaN(id));
        setSelectedPlaces(accessIds);
      } else {
        setSelectedPlaces([]);
      }

      // Set salary type and amounts
      const empTypeSalary = employee.typeSalary || 1;
      setTypeSalary(empTypeSalary);

      if (empTypeSalary === 2) {
        // Monthly salary
        setMonthlySalary(employee.monthlySalary?.toString() || "");
        setDailySalary("");
      } else {
        // Daily salary
        setDailySalary(employee.salary?.toString() || "");
        setMonthlySalary("");
      }
    }
  }, [employee]);

  const togglePlaceAccess = (placeId) => {
    setSelectedPlaces((prev) => {
      if (prev.includes(placeId)) {
        return prev.filter((id) => id !== placeId);
      } else {
        return [...prev, placeId];
      }
    });
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    // If switching to regular employee, clear all place access
    if (newType === 1) {
      setSelectedPlaces([]);
    }
    // If switching to manager, select all places by default
    else if (newType === 2 && allData?.myPlaces) {
      setSelectedPlaces(allData.myPlaces.map((p) => p.id));
    }
  };

  const handleUpdate = async () => {
    if (!name.trim()) {
      alert("Please enter employee name");
      return;
    }

    // Validate based on salary type
    if (typeSalary === 1) {
      if (!dailySalary.trim() || isNaN(dailySalary)) {
        alert("Please enter a valid daily salary");
        return;
      }
    } else {
      if (!monthlySalary.trim() || isNaN(monthlySalary)) {
        alert("Please enter a valid monthly salary");
        return;
      }
    }

    if (!password.trim()) {
      alert("Please enter password");
      return;
    }

    setLoading(true);
    try {
      const accessForPlacesString = selectedPlaces.join(",");

      const response = await axios.put(`${MyDomain}Employee/UpdateEmployee`, {
        Id: employee.id,
        Name: name.trim(),
        Salary: typeSalary === 1 ? parseFloat(dailySalary) : 0,
        PassWord: password.trim(),
        Type: type,
        TypeSalary: typeSalary,
        MonthlySalary: typeSalary === 2 ? parseFloat(monthlySalary) : 0,
        Active: active,
        AccessForPlaces: accessForPlacesString,
        AccessForExpenses: accessForExpenses,
      });

      alert("Employee updated successfully!");
      onUpdateSuccess();
      onClose();
    } catch (error) {
      console.error("❌ Error updating employee:", error);
      alert("Failed to update employee");
    } finally {
      setLoading(false);
    }
  };

  const handleSalaryTypeChange = (newTypeSalary) => {
    setTypeSalary(newTypeSalary);
    // Clear the opposite salary field when switching
    if (newTypeSalary === 1) {
      setMonthlySalary("");
    } else {
      setDailySalary("");
    }
  };

  const isFormValid =
    name.trim() &&
    password.trim() &&
    ((typeSalary === 1 &&
      dailySalary &&
      !isNaN(dailySalary) &&
      parseFloat(dailySalary) > -1) ||
      (typeSalary === 2 &&
        monthlySalary &&
        !isNaN(monthlySalary) &&
        parseFloat(monthlySalary) > -1));

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center">
              <Edit3 className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Update Employee</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 hover:bg-gray-100 rounded-full flex items-center justify-center transition"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-5">
            {/* Employee ID (Read-only) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee ID
              </label>
              <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3 border border-gray-200">
                <Fingerprint className="w-5 h-5 text-gray-500" />
                <span className="text-gray-600 font-semibold">
                  #{employee?.id}
                </span>
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee Name
              </label>
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-purple-500 transition">
                <User className="w-5 h-5 text-gray-600" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter employee name"
                  className="flex-1 bg-transparent outline-none text-gray-900"
                />
              </div>
            </div>

            {/* Active Status Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee Status
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActive(1)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    active === 1
                      ? "bg-green-50 border-green-500 text-green-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Active</span>
                  {active === 1 && <CheckCircle className="w-4 h-4" />}
                </button>

                <button
                  onClick={() => setActive(2)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    active === 2
                      ? "bg-red-50 border-red-500 text-red-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <XCircle className="w-5 h-5" />
                  <span>Inactive</span>
                  {active === 2 && <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Salary Type Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Salary Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSalaryTypeChange(1)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    typeSalary === 1
                      ? "bg-green-50 border-green-500 text-green-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Calendar className="w-5 h-5" />
                  <span>Daily</span>
                  {typeSalary === 1 && <CheckCircle className="w-4 h-4" />}
                </button>

                <button
                  onClick={() => handleSalaryTypeChange(2)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    typeSalary === 2
                      ? "bg-purple-50 border-purple-500 text-purple-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <CalendarDays className="w-5 h-5" />
                  <span>Monthly</span>
                  {typeSalary === 2 && <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Conditional Salary Input */}
            {typeSalary === 1 ? (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Daily Salary (₪)
                </label>
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-green-500 transition">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <input
                    type="number"
                    value={dailySalary}
                    onChange={(e) => setDailySalary(e.target.value)}
                    placeholder="Enter daily salary"
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Salary (₪)
                </label>
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-purple-500 transition">
                  <Wallet className="w-5 h-5 text-purple-600" />
                  <input
                    type="number"
                    value={monthlySalary}
                    onChange={(e) => setMonthlySalary(e.target.value)}
                    placeholder="Enter monthly salary"
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                </div>
              </div>
            )}

            {/* Password Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border-2 border-gray-200 focus-within:border-purple-500 transition">
                <Lock className="w-5 h-5 text-gray-600" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="flex-1 bg-transparent outline-none text-gray-900"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 hover:bg-gray-200 rounded transition"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-600" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Access For Expenses Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expenses Access
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setAccessForExpenses(1)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    accessForExpenses === 1
                      ? "bg-orange-50 border-orange-500 text-orange-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Receipt className="w-5 h-5" />
                  <span>Has Access</span>
                  {accessForExpenses === 1 && (
                    <CheckCircle className="w-4 h-4" />
                  )}
                </button>

                <button
                  onClick={() => setAccessForExpenses(2)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    accessForExpenses === 2
                      ? "bg-gray-100 border-gray-400 text-gray-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Ban className="w-5 h-5" />
                  <span>No Access</span>
                  {accessForExpenses === 2 && (
                    <CheckCircle className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Employee Type Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleTypeChange(1)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    type === 1
                      ? "bg-purple-600 border-purple-600 text-white"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span>Employee</span>
                  {type === 1 && <CheckCircle className="w-4 h-4" />}
                </button>

                <button
                  onClick={() => handleTypeChange(2)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    type === 2
                      ? "bg-purple-600 border-purple-600 text-white"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Briefcase className="w-5 h-5" />
                  <span>Manager</span>
                  {type === 2 && <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Place Access (Show only for Manager) */}
            {type === 2 && allData?.myPlaces && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Place Access
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                  {allData.myPlaces.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {allData.myPlaces.map((place) => (
                        <div
                          key={place.id}
                          className="flex items-center justify-between p-4 hover:bg-gray-100 transition"
                        >
                          <div className="flex items-center gap-3">
                            <MapPin className="w-5 h-5 text-purple-600" />
                            <span className="font-medium text-gray-900">
                              {place.place}
                            </span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPlaces.includes(place.id)}
                              onChange={() => togglePlaceAccess(place.id)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 p-6">
                      No places available
                    </p>
                  )}
                </div>
                {selectedPlaces.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 px-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">
                      {selectedPlaces.length} places selected
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-200">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">
                {typeSalary === 1
                  ? "Daily salary will be applied to each working day."
                  : "Monthly salary will be paid once per month regardless of working days."}
              </p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleUpdate}
            disabled={!isFormValid || loading}
            className={`flex-[2] flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold transition ${
              isFormValid && !loading
                ? "bg-purple-600 hover:bg-purple-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Updating...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Update Employee</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
}
